/**
 * @file AppearanceForm.tsx
 * @description Визуальные настройки блока (phase19.8.5 → 19.8.6): эффект
 * появления при скролле + параметры — задержка, длительность, сдвиг
 * (для направленных эффектов), кривая плавности. Общие для всех типов
 * блоков; controlled: value=appearance, onChange(appearance).
 */

import type {
  BlockAppearance,
  BlockEffect,
  BlockEffectEasing,
} from "../../lib/api-site-pages";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Field } from "./fields";

export const EFFECT_LABELS: Record<BlockEffect, string> = {
  none: "Без эффекта",
  fade: "Проявление",
  "fade-up": "Проявление снизу",
  "fade-down": "Проявление сверху",
  "fade-left": "Сдвиг слева",
  "fade-right": "Сдвиг справа",
  zoom: "Приближение",
  "zoom-out": "Отдаление",
  blur: "Из размытия",
  "blur-up": "Размытие + снизу",
  "flip-up": "Переворот снизу",
  rotate: "Лёгкий поворот",
};

const EASING_LABELS: Record<BlockEffectEasing, string> = {
  ease: "Стандартная",
  "ease-out": "Замедление в конце",
  "ease-in-out": "Плавный старт и финиш",
  spring: "Пружина (перелёт)",
};

/** Эффекты со сдвигом — для них показываем параметр «Сдвиг, px». */
const DIRECTIONAL: readonly BlockEffect[] = [
  "fade-up",
  "fade-down",
  "fade-left",
  "fade-right",
  "blur-up",
  "flip-up",
];

/** Числовое поле: пусто → undefined (рендер возьмёт дефолт). */
function NumberField({
  label,
  value,
  placeholder,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number | undefined;
  placeholder: string;
  min: number;
  max: number;
  step: number;
  onChange: (v: number | undefined) => void;
}) {
  return (
    <Field label={label}>
      <Input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === "" ? undefined : Math.round(Number(raw)));
        }}
      />
    </Field>
  );
}

export function AppearanceForm({
  value,
  onChange,
}: {
  value: BlockAppearance;
  onChange: (v: BlockAppearance) => void;
}) {
  const effect = value.effect ?? "none";

  return (
    <div className="flex flex-col gap-2">
      <Field label="Эффект появления при скролле">
        <Select
          value={effect}
          onValueChange={(v) => onChange({ ...value, effect: v as BlockEffect })}
        >
          <SelectTrigger>
            <SelectValue labels={EFFECT_LABELS} />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(EFFECT_LABELS) as BlockEffect[]).map((k) => (
              <SelectItem key={k} value={k}>
                {EFFECT_LABELS[k]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {effect !== "none" ? (
        <div className="grid grid-cols-2 gap-2">
          <NumberField
            label="Задержка, мс"
            value={value.delayMs}
            placeholder="0"
            min={0}
            max={5000}
            step={100}
            onChange={(delayMs) => onChange({ ...value, delayMs })}
          />
          <NumberField
            label="Длительность, мс"
            value={value.durationMs}
            placeholder="900"
            min={100}
            max={5000}
            step={100}
            onChange={(durationMs) => onChange({ ...value, durationMs })}
          />
          {DIRECTIONAL.includes(effect) ? (
            <NumberField
              label="Сдвиг, px"
              value={value.distancePx}
              placeholder="32"
              min={8}
              max={200}
              step={4}
              onChange={(distancePx) => onChange({ ...value, distancePx })}
            />
          ) : null}
          <Field label="Плавность">
            <Select
              value={value.easing ?? "ease"}
              onValueChange={(v) =>
                onChange({ ...value, easing: v as BlockEffectEasing })
              }
            >
              <SelectTrigger>
                <SelectValue labels={EASING_LABELS} />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(EASING_LABELS) as BlockEffectEasing[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {EASING_LABELS[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
      ) : null}
    </div>
  );
}
